import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/api/client';
import styles from './copilot.module.css';

type ProposedAction = {
  id: string;
  type: string;
  title: string;
  description: string;
  status: string;
  riskLevel: string;
  payload: Record<string, unknown>;
  dependencyIds: string[];
  planVersion: number;
};

type ActionSet = {
  id: string;
  planVersion: number;
  status: string;
};

const RISK_LABEL: Record<string, string> = {
  low: 'Laag risico',
  medium: 'Medium risico',
  high: 'Hoog risico — expliciete goedkeuring',
};

export function ActionsPanel({
  projectId,
  planId,
  planStatus,
  planVersion,
}: {
  projectId: string;
  planId: string | null;
  planStatus: string | null;
  planVersion: number | null;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['copilot-actions', projectId, planId],
    enabled: Boolean(planId),
    queryFn: () =>
      apiFetch<{ actionSet: ActionSet | null; actions: ProposedAction[] }>(
        `/api/copilot/plans/${planId}/action-set?project_id=${projectId}`,
      ),
  });

  const prepare = useMutation({
    mutationFn: () =>
      apiFetch<{ actionSet: ActionSet; actions: ProposedAction[] }>(
        `/api/copilot/plans/${planId}/actions/prepare?project_id=${projectId}`,
        { method: 'POST' },
      ),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot-actions', projectId, planId] }),
  });

  const approve = useMutation({
    mutationFn: (actionId: string) =>
      apiFetch(`/api/copilot/actions/${actionId}/approve?project_id=${projectId}`, { method: 'POST' }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot-actions', projectId, planId] }),
  });

  const reject = useMutation({
    mutationFn: (actionId: string) =>
      apiFetch(`/api/copilot/actions/${actionId}/reject?project_id=${projectId}`, {
        method: 'POST',
        body: JSON.stringify({}),
      }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['copilot-actions', projectId, planId] }),
  });

  const execute = useMutation({
    mutationFn: (actionId: string) =>
      apiFetch<{ execution: { status: string; result?: { audienceId?: string } } }>(
        `/api/copilot/actions/${actionId}/execute?project_id=${projectId}`,
        { method: 'POST', body: '{}' },
      ),
    onSuccess: (_data, actionId) => {
      void qc.invalidateQueries({ queryKey: ['copilot-actions', projectId, planId] });
      void qc.invalidateQueries({ queryKey: ['copilot-action-execution', projectId] });
      void qc.invalidateQueries({ queryKey: ['copilot-action-execution', projectId, actionId] });
    },
  });

  const EXECUTABLE_TYPES = new Set(['CREATE_AUDIENCE', 'CREATE_CAMPAIGN_DRAFT']);

  if (!planId) return null;

  const set = q.data?.actionSet;
  const actions = q.data?.actions ?? [];
  const stale = set && planVersion != null && set.planVersion < planVersion;

  return (
    <aside className={styles.contextPanel} aria-label="Voorgestelde acties">
      <h3 className={styles.contextTitle}>Voorgestelde acties</h3>
      {planStatus !== 'ready_for_review' ? (
        <p className={styles.contextMuted}>Acties zijn beschikbaar wanneer het plan klaar voor review is.</p>
      ) : stale ? (
        <p className={styles.contextMuted}>
          Deze voorstellen horen bij plan v{set.planVersion}. Het plan staat op v{planVersion}. Maak een nieuw
          voorstel.
        </p>
      ) : null}

      {planStatus === 'ready_for_review' && !stale ? (
        <button
          type="button"
          className={styles.sessionBtn}
          disabled={prepare.isPending}
          onClick={() => void prepare.mutate()}
        >
          Acties voorbereiden
        </button>
      ) : null}

      {actions.length ? (
        <ol className={styles.contextList}>
          {actions.map((a, idx) => (
            <li key={a.id}>
              <strong>
                {idx + 1}. {a.title}
              </strong>
              <div className={styles.contextMuted}>{RISK_LABEL[a.riskLevel] ?? a.riskLevel}</div>
              <div className={styles.contextMuted}>{a.description}</div>
              <ActionPreview type={a.type} payload={a.payload} />
              <div className={styles.contextMeta}>Status: {a.status}</div>
              {a.status === 'ready_for_review' && set?.status !== 'invalidated' ? (
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button type="button" className={styles.sendBtn} onClick={() => void approve.mutate(a.id)}>
                    Goedkeuren
                  </button>
                  <button type="button" className={styles.sessionBtn} onClick={() => void reject.mutate(a.id)}>
                    Afwijzen
                  </button>
                </div>
              ) : null}
              {a.status === 'approved' && set?.status !== 'invalidated' ? (
                <ApprovedActionFooter
                  projectId={projectId}
                  actions={actions}
                  action={a}
                  executableTypes={EXECUTABLE_TYPES}
                  executing={execute.isPending && execute.variables === a.id}
                  onExecute={() => void execute.mutate(a.id)}
                  executeError={execute.isError && execute.variables === a.id}
                />
              ) : null}
            </li>
          ))}
        </ol>
      ) : (
        <p className={styles.contextMuted}>Nog geen actievoorstel. Gebruik &quot;Acties voorbereiden&quot;.</p>
      )}
    </aside>
  );
}

function executionStatusLabel(status: string): string {
  switch (status) {
    case 'succeeded':
      return 'Uitgevoerd';
    case 'running':
      return 'Wordt uitgevoerd';
    case 'failed':
      return 'Mislukt';
    case 'blocked':
      return 'Geblokkeerd';
    case 'pending':
    case 'queued':
      return 'Uitvoering gepland';
    default:
      return status;
  }
}

function audienceDependencyId(action: ProposedAction): string | undefined {
  if (action.type !== 'CREATE_CAMPAIGN_DRAFT') return undefined;
  const fromPayload = action.payload.audienceActionId;
  if (typeof fromPayload === 'string') return fromPayload;
  return action.dependencyIds[0];
}

function ApprovedActionFooter({
  projectId,
  actions,
  action,
  executableTypes,
  executing,
  onExecute,
  executeError,
}: {
  projectId: string;
  actions: ProposedAction[];
  action: ProposedAction;
  executableTypes: Set<string>;
  executing: boolean;
  onExecute: () => void;
  executeError: boolean;
}) {
  const depId = audienceDependencyId(action);
  const depAction = depId ? actions.find((x) => x.id === depId) : undefined;

  const execQ = useQuery({
    queryKey: ['copilot-action-execution', projectId, action.id],
    queryFn: () =>
      apiFetch<{
        execution: {
          status: string;
          result?: { audienceId?: string; campaignId?: string };
          errorCode?: string;
        } | null;
      }>(`/api/copilot/actions/${action.id}/execution?project_id=${projectId}`),
    enabled: action.status === 'approved',
  });

  const depExecQ = useQuery({
    queryKey: ['copilot-action-execution', projectId, depId],
    queryFn: () =>
      apiFetch<{ execution: { status: string } | null }>(
        `/api/copilot/actions/${depId}/execution?project_id=${projectId}`,
      ),
    enabled: action.status === 'approved' && Boolean(depId),
  });

  const execution = execQ.data?.execution;
  const depSucceeded = depExecQ.data?.execution?.status === 'succeeded';
  const isExecutableType = executableTypes.has(action.type);
  const alreadySucceeded = execution?.status === 'succeeded';

  let canExecute = isExecutableType && !alreadySucceeded;
  if (action.type === 'CREATE_CAMPAIGN_DRAFT' && depId && !depSucceeded) {
    canExecute = false;
  }

  const confirmMessage =
    action.type === 'CREATE_CAMPAIGN_DRAFT'
      ? 'Je staat op het punt een goedgekeurd campagneconcept aan te maken (geen verzending). Doorgaan?'
      : 'Je staat op het punt deze goedgekeurde actie uit te voeren. Doorgaan?';

  return (
    <div style={{ marginTop: 8 }}>
      <p className={styles.contextMuted}>Goedgekeurd — uitvoering is een aparte stap.</p>
      {action.type === 'CREATE_CAMPAIGN_DRAFT' && depAction ? (
        <p className={styles.contextMuted}>
          {depSucceeded
            ? 'Doelgroep is uitgevoerd — campagneconcept kan worden aangemaakt.'
            : 'Wacht op doelgroep: maak eerst de goedgekeurde doelgroep aan.'}
        </p>
      ) : null}
      {execution ? (
        <p className={styles.contextMeta}>
          Uitvoering: {executionStatusLabel(execution.status)}
          {execution.result?.audienceId ? ` (doelgroep ${execution.result.audienceId.slice(0, 8)}…)` : null}
          {execution.result?.campaignId ? ` (campagne ${execution.result.campaignId.slice(0, 8)}…)` : null}
        </p>
      ) : null}
      {execution?.status === 'blocked' && action.type === 'CREATE_CAMPAIGN_DRAFT' ? (
        <p className={styles.contextMuted}>Geblokkeerd — maak eerst de goedgekeurde doelgroep aan.</p>
      ) : null}
      {canExecute ? (
        <button
          type="button"
          className={styles.sendBtn}
          disabled={executing}
          onClick={() => {
            if (!window.confirm(confirmMessage)) return;
            onExecute();
          }}
        >
          {executing ? 'Bezig…' : 'Uitvoeren'}
        </button>
      ) : !isExecutableType ? (
        <p className={styles.contextMuted}>Uitvoering voor dit actietype volgt in een volgende release.</p>
      ) : null}
      {executeError ? (
        <p className={styles.contextMuted}>Uitvoering mislukt. Er is niets opnieuw gestart zonder controle.</p>
      ) : null}
    </div>
  );
}

function ActionPreview({ type, payload }: { type: string; payload: Record<string, unknown> }) {
  if (type === 'CREATE_AUDIENCE') {
    const sp = payload.sourcePlan as Record<string, unknown> | undefined;
    return (
      <ul className={styles.contextList}>
        <li>Naam: {String(payload.name ?? '')}</li>
        {sp?.locations ? <li>Regio: {(sp.locations as string[]).join(', ')}</li> : null}
        {sp?.companySize ? (
          <li>
            Bedrijfsgrootte: {(sp.companySize as { min: number; max: number }).min}–
            {(sp.companySize as { min: number; max: number }).max}
          </li>
        ) : null}
        {sp?.jobRoles ? <li>Functies: {(sp.jobRoles as string[]).join(', ')}</li> : null}
        {sp?.exclusions ? <li>Uitsluiten: {(sp.exclusions as string[]).join(', ')}</li> : null}
      </ul>
    );
  }
  if (type === 'CREATE_CAMPAIGN_DRAFT') {
    return (
      <ul className={styles.contextList}>
        <li>Naam: {String(payload.name ?? '')}</li>
        <li>Kanaal: {String(payload.channel ?? '')}</li>
        {payload.offer ? <li>Aanbod: {String(payload.offer)}</li> : null}
        {payload.callToAction ? <li>CTA: {String(payload.callToAction)}</li> : null}
      </ul>
    );
  }
  if (type === 'CREATE_SEQUENCE') {
    const steps = (payload.steps as unknown[]) ?? [];
    return <p className={styles.contextMuted}>{steps.length} sequence-stappen (concept)</p>;
  }
  if (type === 'CREATE_TEMPLATE_DRAFT') {
    const t = (payload.templates as unknown[]) ?? [];
    return <p className={styles.contextMuted}>{t.length} template-concepten</p>;
  }
  if (type === 'PREPARE_ENROLLMENT') {
    return <p className={styles.contextMuted}>Enrollment-preview max {String(payload.maxLeads ?? '?')} leads</p>;
  }
  return null;
}
